import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type SegmentedControlSize = 'xs' | 'compact' | 'sm' | 'default';

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** A count beside the label (filters: how many each would show). Pass '…' while it loads. */
  count?: ReactNode;
  /** An icon before the label. */
  icon?: ReactNode;
  /** Hover text for this option (the shared `data-hint`). */
  hint?: string;
  /** The option's accessible name, when its label isn't text. */
  ariaLabel?: string;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  /** The chosen option; one that matches no option (or null) leaves none chosen. */
  value: T | null;
  onValueChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  /** Heights match Button's: xs 24, compact 28, sm 32, default 36. */
  size?: SegmentedControlSize;
  /** What is being chosen. Give this or `aria-labelledby`. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** Every option the same width across the space it has (narrow panels). */
  fullWidth?: boolean;
  /**
   * Let the options run onto more lines rather than overflow: long labels or many options that may
   * not fit a phone's width (the track never grows past its container).
   */
  wrap?: boolean;
  disabled?: boolean;
  /** Layout only (margins, width, flex placement). */
  className?: string;
}

const TRACK: Record<SegmentedControlSize, string> = {
  xs: 'min-h-6 text-xs',
  compact: 'min-h-7 text-xs',
  sm: 'min-h-8 text-sm',
  default: 'min-h-9 text-sm',
};

const ITEM: Record<SegmentedControlSize, string> = {
  xs: 'h-[18px] gap-1 px-2',
  compact: 'h-[22px] gap-1 px-2.5',
  sm: 'h-[26px] gap-1.5 px-2.5',
  default: 'h-[30px] gap-1.5 px-3',
};

/**
 * One choice out of a few, shown side by side: a filter (All, Draft, Agreed), a chart's display, a
 * setting with a handful of values. It is a radio group: one Tab stop, the arrow keys move the choice,
 * Home and End jump. When each option shows different content (Reply / Internal note), use `Tabs`.
 */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  size = 'sm',
  fullWidth = false,
  wrap = false,
  disabled = false,
  className,
  ...aria
}: SegmentedControlProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const enabled = (i: number) => !disabled && !options[i]?.disabled;
  const selectedIndex = options.findIndex((o) => o.value === value);
  // The one Tab stop: the chosen option, else the first that can be chosen.
  const tabStop =
    selectedIndex >= 0 && enabled(selectedIndex) ? selectedIndex : options.findIndex((_, i) => enabled(i));

  const choose = (i: number) => {
    refs.current[i]?.focus();
    if (options[i].value !== value) onValueChange(options[i].value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, from: number) => {
    const n = options.length;
    const step = (dir: 1 | -1, start: number) => {
      for (let k = 1, i = start; k <= n; k++) {
        i = (i + dir + n) % n;
        if (enabled(i)) return i;
      }
      return -1;
    };
    let next = -1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = step(1, from);
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = step(-1, from);
    else if (event.key === 'Home') next = step(1, -1);
    else if (event.key === 'End') next = step(-1, n);
    else return;
    event.preventDefault();
    if (next >= 0) choose(next);
  };

  return (
    <div
      role="radiogroup"
      aria-disabled={disabled || undefined}
      {...aria}
      className={cn(
        'items-center rounded-lg bg-muted p-[3px] text-muted-foreground',
        fullWidth ? 'flex w-full' : 'inline-flex w-fit',
        wrap ? 'max-w-full flex-wrap gap-y-[3px]' : 'flex-nowrap',
        TRACK[size],
        className
      )}
    >
      {options.map((option, i) => {
        const checked = i === selectedIndex;
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={option.ariaLabel}
            data-state={checked ? 'on' : 'off'}
            data-hint={option.hint}
            disabled={!enabled(i)}
            tabIndex={i === tabStop ? 0 : -1}
            onClick={() => choose(i)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              'inline-flex shrink-0 items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors',
              'focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring',
              'disabled:pointer-events-none disabled:opacity-50',
              '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-3.5',
              checked ? 'bg-background text-foreground shadow-sm' : 'text-foreground/60 hover:text-foreground',
              fullWidth && 'min-w-0 flex-1',
              ITEM[size]
            )}
          >
            {option.icon}
            {option.label}
            {option.count != null ? (
              <span className={cn('tabular-nums', checked ? 'text-muted-foreground' : 'opacity-70')}>
                {option.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
